<template>
  <div class="flex flex-col justify-start items-center">
    <div v-if="authenticated || passwordResetResponse" class="w-full">
      <div v-if="passwordResetResponse">
        <h5 class="w-full">
          We sent <span class="text-cw-300">{{ emailResetInput }}</span> a reset
          link.
        </h5>
        <button class="btn btn-cw-700 m-0" @click="closeAndReset">Okay</button>
      </div>
      <div v-else>
        <div
          v-if="authenticated.invalid"
          class="flex flex-col items-center justify-center"
        >
          <h2 class="w-full mb-8 text-center">Invalid credentials</h2>
          <button class="btn btn-cw-700 m-0 font-primary" @click="resetForm">
            Try again
          </button>
        </div>
        <div
          v-if="authenticated.redirect || authenticated.email"
          class="flex flex-col items-center justify-center"
        >
          <div v-if="authenticated.redirect || isOrganisation">
            <h2 class="w-full mb-8 text-center">
              {{ isOrganisation ? 'Welcome back' : 'Active session found' }}
            </h2>
            <a
              :class="
                client === 'client1' || isOrganisation
                  ? 'hover:text-cw-alt'
                  : 'hover:text-expo_red'
              "
              class="btn btn-cw-700 m-0 no-underline font-primary"
              type="submit"
              @click="routeToDashboard"
              >Go to your dashboard</a
            >
          </div>
          <form
            v-else
            id="authenticate"
            ref="authenticate"
            class="flex flex-col items-center justify-center"
            method="POST"
            action="/account/authenticate"
          >
            <h2 class="text-center mb-8">Welcome back.</h2>
            <input type="hidden" name="_token" :value="authenticated.csrf" />
            <input
              type="hidden"
              name="email"
              :value="authenticated.email"
              required=""
            />
            <input
              type="hidden"
              name="password"
              :value="authenticated.password"
              required=""
            />
            <button class="btn btn-cw-700 m-0" type="submit">
              Go to your dashboard
            </button>
          </form>
        </div>
      </div>
    </div>
    <div v-else class="w-full">
      <h2>
        <span v-if="!resetPassword" class="font-primary"
          >{{ isOrganisation ? 'Contributor' : 'Expo' }} Login</span
        >
        <span v-if="resetPassword" class="font-primary">Reset Password</span>
      </h2>

      <div v-if="!resetPassword" class="pl-0 w-full mb-6">
        <h6 class="border-b border-truegray-300 text-truegray-500 pb-4">
          You are signing in to
          {{ isOrganisation ? 'the Contributor Dashboard' : 'Expo' }}. Instead
          sign in to {{ isOrganisation ? '' : 'the ' }}
          <a
            class="text-sm font-semibold underline hover:text-cw-alt"
            @click="isOrganisation = !isOrganisation"
            >{{ isOrganisation ? 'Expo' : 'Contributor Dashboard' }}</a
          >.
        </h6>
      </div>

      <!-- SIGN IN INPUT -->
      <div
        v-if="!resetPassword && !emailSubmitResponse"
        class="w-full relative"
      >
        <div class="w-full">
          <p>Email</p>
          <input
            id="email-address"
            v-model="emailInput"
            :class="'placeholder-truegray-300 text-gray-600 focus:text-gray-600 visited:text-gray-600 focus:ring-gray-600'"
            class="flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-truegray-300 rounded-lg relative bg-white border border-truegray-300 mb-4"
            name="email"
            type="email"
            autocomplete="off"
            placeholder="example@example.com"
            @keyup="checkValidation"
            @change="checkValidation"
          />
        </div>
        <div class="w-full">
          <!-- TO DO - hook up password instead of email validation -->
          <p>Password</p>
          <div
            class="flex lg:flex-nowrap flex-wrap space-x-2 items-center justify-center"
          >
            <input
              id="password"
              v-model="passwordInput"
              :class="'placeholder-truegray-300 text-gray-600 focus:text-gray-600 visited:text-gray-600 focus:ring-gray-600'"
              class="flex-grow px-4 outline-none focus:outline-none bg-transparent h-12 font-primary text-black focus:placeholder-truegray-300 rounded-lg relative bg-white border border-truegray-300 w-full lg:w-3/4"
              name="password"
              type="password"
              autocomplete="off"
              placeholder="enter your password"
              @keyup="checkValidation"
              @change="checkValidation"
            />
            <!-- SIGN IN BUTTON -->
            <div v-if="!emailSubmitResponse" class="relative text-left">
              <button
                :disabled="!canSubmit"
                :class="[
                  !canSubmit
                    ? 'opacity-25 border border-black text-black cursor-not-allowed'
                    : 'btn-cw-700',
                  thinking ? '' : '',
                ]"
                class="relative rounded-lg py-4 px-8 text-white font-primary text-center ease-in-out duration-300 h-12 btn m-0 mt-8 lg:mt-0 lg:w-auto w-60"
                @click="submitLogin"
              >
                <div class="flex justify-center items-center w-full h-full">
                  <div
                    class="absolute inset-0 w-full flex justify-center items-center z-20"
                  >
                    <lottie
                      v-if="thinking"
                      :options="loadingAnimationOptions"
                    />
                  </div>
                  <div
                    :class="[thinking ? 'invisible' : '']"
                    class="w-full flex items-center justify-center font-primary font-normal"
                  >
                    Submit
                  </div>
                </div>
              </button>
            </div>
            <!-- /SIGN IN BUTTON -->
          </div>
        </div>
      </div>

      <!-- RESET INPUT -->
      <div v-if="resetPassword" class="w-full relative">
        <div class="w-full"><p>Email</p></div>
        <div class="w-full">
          <!-- TO DO - hook up password instead of email validation -->
          <div
            class="flex flex-wrap lg:flex-nowrap lg:space-x-2 justify-center items-center"
          >
            <input
              id="email-address"
              v-model="emailResetInput"
              :class="'placeholder-truegray-300 text-gray-600 focus:text-gray-600 visited:text-gray-600 focus:ring-gray-600'"
              class="flex-grow px-4 m-0 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-truegray-300 rounded-lg relative bg-white border border-truegray-300"
              name="email"
              type="email"
              autocomplete="off"
              placeholder="example@example.com"
              @keyup="checkValidation"
              @change="checkValidation"
            />
            <!-- RESET BUTTON -->
            <div class="relative text-left w-full lg:w-auto">
              <button
                :disabled="!canSubmit"
                :class="[
                  !canSubmit
                    ? 'opacity-25 border border-black text-black cursor-not-allowed'
                    : 'btn-cw-700',
                  thinking ? '' : '',
                ]"
                class="relative rounded-lg py-4 px-8 text-white font-primary text-center ease-in-out duration-300 h-12 btn m-0 mt-6 lg:mt-0 w-full lg:w-auto"
                @click="submitReset"
              >
                <div class="flex justify-center items-center w-full h-full">
                  <div
                    class="absolute inset-0 w-full flex justify-center items-center z-20"
                  >
                    <lottie
                      v-if="thinking"
                      :options="loadingAnimationOptions"
                    />
                  </div>
                  <div
                    :class="[thinking ? 'invisible' : '']"
                    class="w-full flex items-center justify-center font-primary font-normal"
                  >
                    Submit
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!resetPassword"
        class="border-t border-truegray-300 p-4 pl-0 w-full mt-6"
      >
        <h6 class="text-truegray-500" @click="resetPassword = !resetPassword">
          Forgot your password?
          <a
            :class="
              client === 'client1' ? 'hover:text-cw-alt' : 'hover:text-expo_red'
            "
            class="text-sm font-semibold underline"
            >click here</a
          >
        </h6>
      </div>

      <div
        v-if="resetPassword"
        class="border-t border-truegray-300 p-4 pl-0 w-full mt-6"
      >
        <h6 class="text-truegray-500" @click="toggleResetPassword">
          <a
            :class="
              client === 'client1' ? 'hover:text-cw-alt' : 'hover:text-expo_red'
            "
            class="text-sm font-semibold underline"
            >Cancel</a
          >
        </h6>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { shared } from '../../js/mixins/shared.js'
import { EventBus } from '../../js/plugins/event-bus.js'
import * as loadingAnimation from '../../shared/components/loading.json'

export default {
  mixins: [shared],

  data() {
    return {
      authenticated: false,
      passwordResetResponse: false,
      csrf: false,

      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,

      canSubmit: false,
      thinking: false,
      rememberMe: 0,
      emailInput: '',
      passwordInput: '',
      emailResetInput: '',
      userAlreadyRegistered: false,
      emailSubmitResponse: false,
      resetPassword: false,
      isOrganisation: true,
    }
  },

  computed: {
    ...mapGetters({
      client: 'shared/getClient',
    }),
  },

  watch: {
    emailInput: {
      handler: function (val) {
        // Remove all white space from inputs...
        val = val.replace(/\s+/g, '') // or you can use your own preferred way to remove leading spaces
        this.$set(this, 'emailInput', val)
      },

      deep: true,
    },

    passwordInput: {
      handler: function (val) {
        // Remove all white space from inputs...
        val = val.replace(/\s+/g, '') // or you can use your own preferred way to remove leading spaces
        this.$set(this, 'passwordInput', val)
      },

      deep: true,
    },

    emailResetInput: {
      handler: function (val) {
        // Remove all white space from inputs...
        val = val.replace(/\s+/g, '') // or you can use your own preferred way to remove leading spaces
        this.$set(this, 'emailResetInput', val)
      },

      deep: true,
    },
  },

  mounted() {
    let authenticated = document.getElementsByTagName('body')[0].dataset.admin
      ? true
      : false

    if (authenticated) {
      this.$set(this, 'authenticated', { redirect: true })
    }

    let token = document.head.querySelector('meta[name="csrf-token"]')
    if (token) {
      let csrf = token.getAttribute('content')
      this.$set(this, 'csrf', csrf)
    }

    // When the theme changes dynamically we also need to changed stored theme data variables...
    EventBus.$on('validateSignInEmail', (email) => {
      this.$set(this, 'emailInput', email)
      this.validateSignInEmail()
    })

    // When the theme changes dynamically we also need to changed stored theme data variables...
    EventBus.$on('modalAlertClose', () => {
      this.$set(this, 'emailSubmitResponse', false)
      this.$set(this, 'emailInput', '')
    })
  },

  methods: {
    resetForm() {
      this.$set(this, 'resetPassword', false)
      this.$set(this, 'emailSubmitResponse', false)
      this.$set(this, 'emailInput', '')
      this.$set(this, 'emailResetInput', '')
      this.$set(this, 'passwordInput', '')

      this.$set(this, 'authenticated', false)
      this.$set(this, 'passwordResetResponse', false)
    },

    closeAndReset() {
      EventBus.$emit('showModal', false)
    },

    checkValidation() {
      let canSubmit = true
      // Enable / dissable sign in button depending on the validity of the email they have provided
      if (!this.resetPassword) {
        if (
          !this.validateEmail(this.emailInput) ||
          this.passwordInput.length < 3
        ) {
          canSubmit = false
        }
      } else {
        if (!this.validateEmail(this.emailResetInput)) {
          canSubmit = false
        }
      }
      this.$set(this, 'canSubmit', canSubmit)
    },

    routeToDashboard() {
      this.$router.push({
        path: this.isOrganisation ? '/cloud/dashboard' : '/account',
      })

      this.closeAndReset()
    },

    submitLogin() {
      this.thinking = true

      let rememberMe = this.rememberMe // Default the expiry token to be in a years time!
      axios
        .post('/central-api/login', {
          csrf: this.csrf,
          email: this.emailInput,
          password: this.passwordInput,
          rememberMe: rememberMe,
          organisation: this.isOrganisation,
        })
        .then(async (response) => {
          const returned = response.data
          this.$set(this, 'emailSubmitResponse', true)
          this.$set(this, 'thinking', false)
          this.$set(this, 'canSubmit', false)

          if (returned.success && returned.csrf) {
            this.$set(this, 'authenticated', {
              csrf: returned.csrf,
              email: this.emailInput,
              password: this.passwordInput,
            })

            if (this.isOrganisation) {
              // set oauth session and init organisation
              await auth.signIn(
                returned.access_token,
                returned.user,
                returned.expires,
              )
            }
          } else {
            this.$set(this, 'authenticated', { invalid: true })
          }
          if (returned.alert) {
            EventBus.$emit('triggerAlertResponse', {
              status: returned.alert.status,
              message: returned.alert.message,
            })
          }
        })
    },

    submitReset() {
      this.thinking = true

      axios
        .post('/central-api/reset-password', { email: this.emailResetInput })
        .then((response) => {
          this.$set(this, 'emailSubmitResponse', true)
          this.$set(this, 'thinking', false)
          this.$set(this, 'canSubmit', true)

          let returned = response.data

          if (returned.success) {
            this.$set(this, 'passwordResetResponse', true)
          } else {
            this.$set(this, 'authenticated', { invalid: true })
            this.$set(this, 'passwordResetResponse', false)
            EventBus.$emit('triggerAlertResponse', {
              status: 'error',
              message: 'Something went wrong!',
            })
          }
        })
    },

    toggleResetPassword() {
      let resetStatus = !this.resetPassword
      this.$set(this, 'resetPassword', resetStatus)
    },
  },
}
</script>
